import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

// Import components
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import MoodTracker from './pages/MoodTracker';
import Matches from './pages/Matches';
import Messages from './pages/Messages';
import SupportGroups from './pages/SupportGroups';
import GroupChat from './pages/GroupChat';
import Profile from './pages/Profile';
import WellnessResources from './pages/WellnessResources';
import RelaxGames from './pages/RelaxGames';
import PhotoManager from './pages/PhotoManager';
import Settings from './pages/Settings';
import FAQ from './pages/FAQ';
import CounselorPage from './pages/CounselorPage';
import AdminDashboard from './pages/AdminDashboard';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios defaults
axios.defaults.baseURL = API;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get('/users/me');
      setUser(response.data);
      // Store user data in localStorage for fallback
      localStorage.setItem('userData', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only clear tokens on actual authentication failures (401/403)
      // Don't clear on network errors, server errors, etc.
      if (error.response && [401, 403].includes(error.response.status)) {
        console.log('Authentication failed, clearing tokens');
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      } else {
        console.log('Non-authentication error occurred, keeping user session');
        // For non-auth errors, try to use cached user data if available
        const cachedUserData = localStorage.getItem('userData');
        if (cachedUserData) {
          try {
            const userData = JSON.parse(cachedUserData);
            setUser(userData);
            console.log('Using cached user data for session continuity');
          } catch (parseError) {
            console.error('Error parsing cached user data:', parseError);
            // If cached data is corrupt, we'll still keep the token but set user to null
            // This will allow retry on next page load
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading eternitysingles...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300">
          {user && <Navbar user={user} onLogout={logout} />}
          
          <Routes>
            <Route 
              path="/" 
              element={user ? <Navigate to="/dashboard" /> : <LandingPage />} 
            />
            <Route 
              path="/auth" 
              element={user ? <Navigate to="/dashboard" /> : <AuthPage onLogin={login} />} 
            />
            
            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={user ? <Dashboard user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/mood" 
              element={user ? <MoodTracker user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/matches" 
              element={user ? <Matches user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/messages" 
              element={user ? <Messages user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/groups" 
              element={user ? <SupportGroups user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/groups/:groupId/chat" 
              element={user ? <GroupChat user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/profile" 
              element={user ? <Profile user={user} onUserUpdate={setUser} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/wellness" 
              element={user ? <WellnessResources /> : <Navigate to="/" />} 
            />
            <Route 
              path="/relax" 
              element={user ? <RelaxGames user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/settings" 
              element={user ? <Settings user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/faq" 
              element={<FAQ />} 
            />
            <Route 
              path="/counselor" 
              element={user ? <CounselorPage user={user} /> : <Navigate to="/" />} 
            />
            <Route 
              path="/admin" 
              element={user?.is_admin ? <AdminDashboard user={user} /> : <Navigate to="/dashboard" />} 
            />
          </Routes>
          
          <Toaster />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;